import {provide, inject, type InjectionKey, type App} from "vue";
import {FormEnvironment, EventBus, EventBusRegistry, EventBusListener, CommandHandler, CommandRegistry, UnregisterHandler, CatchAllCommandsRegistry} from "./Types";
import {UserInfo} from "@t/user/UserInfo.ts";

export const envKey = Symbol() as InjectionKey<FormEnvironment>;
export const eventBusKey = Symbol() as InjectionKey<EventBus>;


export function provideEnv(env: FormEnvironment):void {
    provide(envKey, env);
}

export function provideNewEventBus():EventBus {
    const eb = createEventBus();
    provide(eventBusKey, eb);
    return eb;
}

export function provideEventBus(app:App, eb?:EventBus):EventBus {
    if(!eb) eb = createEventBus();
    app.provide(eventBusKey, eb);
    return eb;
}

const BLACK_HOLE_EVENT_BUS:EventBus = {
    on(eventName:string, listener: EventBusListener):number { return 1; },
    off(subscription: number) {},
    fire(eventName: string, ...args:any[]) {},

	onCommand(command: string|string[], handler: CommandHandler) { return () => {}; },
	onAnyCommand(handler: CommandHandler) { return () => {}; },

	async executeCommand(command: string, ...args: any[]):Promise<boolean> { return false; },
}

export function injectEventBus():EventBus {
    return inject(eventBusKey, BLACK_HOLE_EVENT_BUS);
}

export function createEventBus():EventBus {
    const registry: EventBusRegistry = {};
    let nextReg = 1;
	const commands:CommandRegistry = {};
	let catchAllId = 0;
	const catchAllCommands:CatchAllCommandsRegistry = {};

    function on(eventName:string, listener: EventBusListener): number {
        if (registry[eventName] === undefined) registry[eventName] = [];
        const regNum = nextReg++;
        registry[eventName].push({ callback: listener, regNum });
        return regNum;
    }

    function off(subscription: number):boolean {
        for (let eventName of Object.keys(registry)) {
            const listeners = registry[eventName];
            if (listeners === undefined) continue;
            for (let i = 0; i < listeners.length; i++) {
                if (listeners[i].regNum === subscription) {
                    listeners.splice(i, 1);
                    return true;
                }
            }
        }
        console.warn("EventBus was asked to remove listener with unknown ID " + subscription);
        return false;
    }

    function fire(eventName: string, ...args:any[]):void {
        const listeners = registry[eventName];
        if (listeners) {
            for (let listener of listeners) {
                listener.callback(...args);
            }
        }
    }

	function _onCommand(command: string, handler: CommandHandler):void {
		const alreadyRegistered = typeof commands[command] !== "undefined";
		commands[command] = handler;
		if (alreadyRegistered) console.warn("Command "+command+" already registered (replaced with new handler)");
	}

	function _unregisterCommands(...commands: string[]):void {
		for (let c of commands) {
			delete commands[c];
		}
	}

	function onCommand(command: string|string[], handler: CommandHandler):UnregisterHandler {
		if (Array.isArray(command)) {
			command.forEach(c => _onCommand(c, handler));
			return () => { _unregisterCommands(...command); };
		}
		_onCommand(command, handler);
		return () => { _unregisterCommands(command); };
	}

	function onAnyCommand(handler: CommandHandler):UnregisterHandler {
		const thisID = catchAllId++;
		catchAllCommands[thisID] = handler;
		return () => { delete catchAllCommands[thisID]; };
	}


	async function executeCommand(command: string, ...args: any[]):Promise<boolean> {
		const handler = commands[command];
		if (handler) {
			const result = await handler(command, ...args);
			if (result !== "pass") return result === "ok";
		}
		// Not a concrete handler - try catch-all
		for (let handler of Object.values(catchAllCommands)) {
			const result = await handler(command, ...args);
			if (result !== "pass") return result === "ok";
		}
		return false;
	}

    return { on, off, fire, onCommand, onAnyCommand, executeCommand };
}


export function injectEnv():FormEnvironment {
    let env = inject(envKey);
    if (typeof env === "undefined") throw "Unknown environment (You must provide FormEnvironment instance)";
    return env;
}

export function userInRole(user: UserInfo, role:string) {
	if (Array.isArray(user.roles)) {
		return user.roles.indexOf(role) >= 0;
	}
	return false;
}

export function userInAllRoles(user: UserInfo, ...roles:string[]) {
	for (let r of roles) {
		if (!userInRole(user, r)) return false;
	}
	return true;
}

export function userInAnyRole(user: UserInfo, ...roles:string[]) {
	for (let r of roles) {
		if (userInRole(user, r)) return true;
	}
	return false;
}
